import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import {
    ResponsiveContainer,
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
    ZAxis,
} from 'recharts';

export const AdSetEfficiencyScatterChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, entities, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance || !entities) return [];

        // Use detailedBreakdown which preserves original (non-remapped) entity IDs
        const breakdownSource = finance.detailedBreakdown || finance.breakdown || [];

        const adSetEntities = entities.filter(
            (e: any) => e.entityType === 'ad_set' || e.entityType === 'ad_group'
        );
        if (adSetEntities.length === 0) return [];

        const adSetIds = new Set(adSetEntities.map((e: any) => e.id));
        const nameMap: Record<string, string> = {};
        for (const e of adSetEntities) {
            nameMap[e.id as string] = (e.name as string) || (e.id as string);
        }

        const spendByEntity: Record<string, number> = {};

        for (const item of breakdownSource) {
            const entityId = (item.entityId || (item as any).parentEntityId) as string;
            if (!adSetIds.has(entityId)) continue;

            const value = Math.abs(Number(item.value) || 0);

            if (item.type === 'EXPENSE' && ['advertising', 'ad_spend', 'marketing'].includes(item.category)) {
                spendByEntity[entityId] = (spendByEntity[entityId] || 0) + value;
            }
        }

        // Attribute revenue proportionally based on each ad set's share of total ad spend
        const totalRevenue = Number(finance.totalRevenues) || 0;
        const totalAdSpend = Number(finance.metrics?.adSpend) || 0;

        return Object.keys(spendByEntity).map((entityId) => {
            const spend = spendByEntity[entityId];
            const revenueShare = totalAdSpend > 0 ? (spend / totalAdSpend) * totalRevenue : 0;
            return {
                name: nameMap[entityId] || entityId,
                spend,
                revenue: Math.round(revenueShare * 100) / 100,
            };
        });
    }, [finance?.detailedBreakdown, finance?.breakdown, finance?.totalRevenues, finance?.metrics?.adSpend, entities]);

    const maxValue = useMemo(() => {
        if (chartData.length === 0) return 100;
        const maxSpend = Math.max(...chartData.map((d) => d.spend));
        const maxRevenue = Math.max(...chartData.map((d) => d.revenue));
        return Math.max(maxSpend, maxRevenue) * 1.1;
    }, [chartData]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            type="number"
                            dataKey="spend"
                            name={t('charts.adSpend', 'Ad Spend')}
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => formatCompactNumber(value, currencySymbol)}
                            domain={[0, maxValue]}
                        />
                        <YAxis
                            type="number"
                            dataKey="revenue"
                            name={t('charts.revenue', 'Revenue')}
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => formatCompactNumber(value, currencySymbol)}
                            domain={[0, maxValue]}
                        />
                        <ZAxis range={[60, 60]} />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={{ strokeDasharray: '3 3' }}
                            formatter={(value: number, name: string) => [
                                formatCompactNumber(value, currencySymbol),
                                name,
                            ]}
                            labelFormatter={(_label: string, payload: Array<any>) => {
                                if (payload.length > 0) {
                                    const data = payload[0].payload as any;
                                    return data.name as string;
                                }
                                return '';
                            }}
                        />
                        <ReferenceLine
                            segment={[
                                { x: 0, y: 0 },
                                { x: maxValue, y: maxValue },
                            ]}
                            stroke={CHART_COLORS.gray[400]}
                            strokeDasharray="4 4"
                            label={{
                                value: t('charts.breakEven', 'Break-even'),
                                position: 'insideTopLeft',
                                fill: CHART_COLORS.gray[500],
                                fontSize: 10,
                            }}
                        />
                        <Scatter
                            data={chartData}
                            fill={CHART_COLORS.primary}
                        />
                    </ScatterChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
